import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { decideGlobalProducerRedirect } from "@/lib/coProducerAccess";
import { BackendUnreachable } from "./BackendUnreachable";

export function ProducerRoute() {
  const {
    session,
    isLoading,
    isRolesLoading,
    isSubscriptionLoading,
    isAuthDataUnavailable,
    isCoProducerLoading,
    isAdmin,
    hasProducerAccess,
    coProducerBriefingIds,
    subscriptionStatus,
    refreshAuthData,
  } = useAuth();

  if (isLoading || isRolesLoading || isSubscriptionLoading || isCoProducerLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/producer-login" replace />;
  }

  // The backend is unreachable and nothing is cached: say so instead of reading
  // "unknown" as "no access" and bouncing the user to /upgrade. A known admin
  // passes through — their access doesn't depend on a subscription.
  if (isAuthDataUnavailable && !isAdmin) {
    return <BackendUnreachable onRetry={refreshAuthData} />;
  }

  // A co-producer-only account must never see this "layer above" (overview /
  // settings / my-team) — bounce them down to their own production.
  const coProducerRedirect = decideGlobalProducerRedirect({
    isAdmin,
    hasProducerAccess,
    coProducerBriefingIds,
  });
  if (coProducerRedirect) {
    return <Navigate to={coProducerRedirect} replace />;
  }

  // Trial user who hasn't chosen a tier yet — send to tier selection
  if (subscriptionStatus?.plan_status === "trial" && !subscriptionStatus.plan_tier) {
    return <Navigate to="/choose-trial" replace />;
  }

  if (!hasProducerAccess) {
    return <Navigate to="/upgrade" replace />;
  }

  return <Outlet />;
}
